// frontend/src/modules/projects/components/MilestonesSection.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Plus, Flag, Check, Trash2, Pencil } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { formatDate, formatMoney } from '../../../design-system/format';
import { useApi } from '../../../design-system/useApi';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { projectsApi } from '../api';
import type { ProjectMilestone } from '../types';

export function MilestonesSection({ projectId, currency }: { projectId: string; currency: string }) {
  const q = useApi<{ milestones: ProjectMilestone[] }>(`/api/projects/projects/${projectId}/milestones`, [projectId]);
  const confirm = useConfirm();
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<ProjectMilestone | null>(null);
  const milestones = q.data?.milestones ?? [];

  async function toggle(m: ProjectMilestone) {
    try {
      await projectsApi.updateMilestone(m.id, { status: m.status === 'completed' ? 'pending' : 'completed' });
      q.reload();
    } catch (err: any) { toast.error('Update failed', { description: err.message }); }
  }

  async function remove(m: ProjectMilestone) {
    if (!(await confirm({ title: `Delete milestone "${m.name}"?`, destructive: true, confirmLabel: 'Delete' }))) return;
    try { await projectsApi.deleteMilestone(m.id); q.reload(); }
    catch (err: any) { toast.error('Delete failed', { description: err.message }); }
  }

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className={VISUAL.dashSection}>Milestones</h3>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}
          className="rounded-full h-9 px-4 text-[12.5px] font-semibold gap-1.5">
          <Plus className="h-3 w-3" />
          Add milestone
        </Button>
      </div>
      <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        {q.loading ? (
          <div className="py-8 px-6 text-[13px] text-muted-foreground">Loading…</div>
        ) : q.error ? (
          <ErrorState title="Couldn't load milestones" description={q.error} onRetry={q.reload} />
        ) : milestones.length === 0 ? (
          <div className="py-10 px-6 text-center">
            <Flag className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
            <p className="text-[14px] font-semibold text-foreground">No milestones</p>
            <p className="text-[13px] text-muted-foreground mt-1">Break the project into deliverables with due dates + amounts.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {milestones.map((m) => (
              <li key={m.id} className="flex items-center gap-3 px-5 py-3.5">
                <button type="button" onClick={() => toggle(m)}
                  className={[
                    'h-5 w-5 rounded-md ring-1 flex items-center justify-center shrink-0 transition-colors',
                    m.status === 'completed' ? 'bg-success/15 ring-success/40 text-success' : 'ring-border/60 text-transparent hover:ring-saffron/50',
                  ].join(' ')}
                  aria-label={m.status === 'completed' ? 'Mark pending' : 'Mark completed'}>
                  <Check className="h-3 w-3" />
                </button>
                <div className="flex-1 min-w-0">
                  <p className={['text-[14px] font-medium truncate', m.status === 'completed' ? 'text-muted-foreground line-through' : 'text-foreground'].join(' ')}>{m.name}</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    {m.due_date ? `Due ${formatDate(m.due_date)}` : 'No due date'}
                    {m.amount != null && <> · {formatMoney(m.amount, currency)}</>}
                  </p>
                </div>
                {m.status === 'completed'
                  ? <span className={statusPill('success')}><span className={statusDot('success')} />Done</span>
                  : <span className={statusPill('muted')}><span className={statusDot('muted')} />Pending</span>}
                <button type="button" onClick={() => setEditing(m)}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1" aria-label={`Edit milestone ${m.name}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => remove(m)}
                  className="text-muted-foreground hover:text-rose-500 transition-colors p-1" aria-label="Delete milestone">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <AddMilestoneDialog open={open} onOpenChange={setOpen} projectId={projectId}
        onAdded={() => { setOpen(false); q.reload(); }} />
      <EditMilestoneDialog milestone={editing} onOpenChange={(v) => { if (!v) setEditing(null); }}
        onSaved={() => { setEditing(null); q.reload(); }} />
    </section>
  );
}

function EditMilestoneDialog({ milestone, onOpenChange, onSaved }: {
  milestone: ProjectMilestone | null; onOpenChange: (v: boolean) => void; onSaved: () => void;
}) {
  const [name, setName] = React.useState('');
  const [dueDate, setDueDate] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  // Seed the form each time a milestone is picked for editing.
  React.useEffect(() => {
    if (milestone) {
      setName(milestone.name);
      setDueDate(milestone.due_date ? milestone.due_date.slice(0, 10) : '');
      setAmount(milestone.amount != null ? String(milestone.amount) : '');
    }
  }, [milestone]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!milestone) return;
    if (!name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      await projectsApi.updateMilestone(milestone.id, {
        name: name.trim(),
        due_date: dueDate || null,
        amount: amount.trim() ? Number(amount) : null,
      });
      onSaved();
    } catch (err: any) {
      toast.error('Update failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={milestone !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit milestone</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Name<span className="text-red-500 dark:text-red-400 ml-0.5">*</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Amount</Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddMilestoneDialog({ open, onOpenChange, projectId, onAdded }: {
  open: boolean; onOpenChange: (v: boolean) => void; projectId: string; onAdded: () => void;
}) {
  const [name, setName] = React.useState('');
  const [dueDate, setDueDate] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => { if (open) { setName(''); setDueDate(''); setAmount(''); } }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      await projectsApi.createMilestone(projectId, {
        name: name.trim(),
        due_date: dueDate || null,
        amount: amount.trim() ? Number(amount) : null,
      });
      onAdded();
    } catch (err: any) {
      toast.error('Create failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>New milestone</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Name<span className="text-red-500 dark:text-red-400 ml-0.5">*</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Amount</Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
